import { AnalyticsPrimitive, AnalyticsProperties } from "./events";

type UnknownRecord = Record<string, any>;

const asRecord = (value: unknown): UnknownRecord =>
  value && typeof value === "object" ? (value as UnknownRecord) : {};

const firstDefined = (...values: unknown[]) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const toIsoDate = (value: unknown): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value as string | number | Date);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const daysUntil = (isoDate?: string): number | undefined => {
  if (!isoDate) {
    return undefined;
  }

  return Math.ceil((new Date(isoDate).getTime() - Date.now()) / 86_400_000);
};

const normalizeKey = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const normalizePrimitive = (value: unknown): AnalyticsPrimitive | undefined => {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null
  ) {
    return value;
  }

  return undefined;
};

const collectStrings = (...values: unknown[]): string[] =>
  Array.from(
    new Set(
      values
        .flatMap((value) => (Array.isArray(value) ? value : []))
        .map((value) =>
          typeof value === "string"
            ? value
            : String(asRecord(value).code || asRecord(value).name || ""),
        )
        .filter(Boolean),
    ),
  ).sort();

export type AnalyticsUserContext = AnalyticsProperties & {
  actor_type: "authenticated" | "anonymous";
  actor_user_id?: string | number;
};

export const buildAnalyticsUserContext = (
  userValue?: unknown,
  userDataValue?: unknown,
): AnalyticsUserContext => {
  const envelope = asRecord(userDataValue);
  const envelopeData = asRecord(envelope.data);
  const root = Object.keys(envelopeData).length > 0 ? envelopeData : envelope;
  const user = {
    ...asRecord(root.user),
    ...asRecord(userValue),
  };
  const subscription = {
    ...asRecord(root.subscription),
    ...asRecord(user.subscription),
  };
  const settings = {
    ...asRecord(root.settings),
    ...asRecord(subscription.settings),
  };

  const userId = firstDefined(user.id, root.userId, root.user_id);
  const expiresAt = toIsoDate(
    firstDefined(
      subscription.expiresAt,
      subscription.expires_at,
      user.paidTill,
      user.testPeriodTill,
    ),
  );
  const subscriptionCode = firstDefined(
    subscription.code,
    subscription.name,
    subscription.planCode,
    root.subscriptionCode,
  );
  const isTrial = Boolean(
    firstDefined(subscription.isTrial, user.testPeriod, root.testPeriod, false),
  );
  const isPaid = Boolean(
    firstDefined(subscription.isPaid, user.paid, root.paid, false),
  );
  const expired = Boolean(expiresAt && new Date(expiresAt).getTime() < Date.now());
  const abilities = collectStrings(
    root.abilities,
    root.roles,
    user.abilities,
    user.roles,
  );

  const context: AnalyticsUserContext = {
    actor_type: userId === undefined ? "anonymous" : "authenticated",
    actor_user_id:
      typeof userId === "string" || typeof userId === "number"
        ? userId
        : undefined,
    actor_agency_id: normalizePrimitive(
      firstDefined(user.agencyId, user.agency_id, root.agencyId, root.agency_id),
    ),
    actor_company_id: normalizePrimitive(
      firstDefined(
        user.companyId,
        user.company_id,
        subscription.companyId,
        subscription.company_id,
      ),
    ),
    actor_company_user: normalizePrimitive(
      firstDefined(subscription.companyUser, subscription.company_user),
    ),
    subscription_code: normalizePrimitive(subscriptionCode),
    subscription_status: expired
      ? "expired"
      : isTrial
        ? "trial"
        : isPaid
          ? "active"
          : subscriptionCode
            ? "limited"
            : "none",
    subscription_expires_at: expiresAt,
    subscription_days_remaining: daysUntil(expiresAt),
    subscription_amount: normalizePrimitive(
      firstDefined(subscription.amount, subscription.price),
    ),
    subscription_currency: normalizePrimitive(subscription.currency),
    subscription_is_trial: isTrial,
    subscription_is_paid: isPaid,
    ability_set: abilities.join("|") || undefined,
  };

  for (const [key, value] of Object.entries(settings)) {
    const normalizedValue = normalizePrimitive(value);
    if (normalizedValue !== undefined) {
      context[`permission_${normalizeKey(key)}`] = normalizedValue;
    }
  }

  for (const ability of abilities) {
    context[`ability_${normalizeKey(ability)}`] = true;
  }

  return context;
};

export interface CalculationLike extends UnknownRecord {}

export const getCalculationAnalyticsProperties = (
  calculation?: CalculationLike | null,
  housing?: UnknownRecord | null,
): AnalyticsProperties => {
  const data = asRecord(calculation);
  const housingData = asRecord(housing);
  const property = asRecord(data.housingData);

  return {
    housing_id: normalizePrimitive(
      firstDefined(
        data.housingId,
        data.housing_id,
        housingData.id,
        property.id,
      ),
    ),
    housing_slug: normalizePrimitive(
      firstDefined(data.housingSlug, housingData.slug, property.slug),
    ),
    property_type: normalizePrimitive(
      firstDefined(data.propertyType, data.type, housingData.type),
    ),
    flat_type: normalizePrimitive(data.flatType),
    floor: normalizePrimitive(data.floor),
    area_sqm: normalizePrimitive(firstDefined(data.area, data.area_sqm)),
    full_price: normalizePrimitive(data.fullPrice),
    first_payment: normalizePrimitive(data.firstPayment),
    monthly_payment: normalizePrimitive(data.monthlyPayment),
    installment_period_months: normalizePrimitive(data.installmentPeriod),
    installment_option: normalizePrimitive(data.installmentOption),
    installment_percent: normalizePrimitive(data.installmentPercent),
    calculation_has_maternity_capital: Boolean(
      firstDefined(data.restAfterMatCap, data.maternityCapital, false),
    ),
  };
};

export const getMarketListingAnalyticsProperties = (
  listing?: UnknownRecord | null,
): AnalyticsProperties => {
  const item = asRecord(listing);

  return {
    listing_id: normalizePrimitive(item.id),
    listing_type: normalizePrimitive(item.type),
    listing_market_type: normalizePrimitive(item.market_type),
    listing_category: normalizePrimitive(item.board_category),
    listing_subcategory: normalizePrimitive(item.board_subcategory),
    listing_owner_user_id: normalizePrimitive(
      firstDefined(item.owner_id, item.ownerId),
    ),
    listing_status_id: normalizePrimitive(item.status_id),
    listing_moderation_status: normalizePrimitive(item.moderation_status),
    listing_area_sqm: normalizePrimitive(item.area_sqm),
    listing_area_site: normalizePrimitive(item.area_site),
    listing_price_sqm: normalizePrimitive(item.price_sqm),
    listing_price_monthly: normalizePrimitive(item.price_monthly),
    listing_agent_commission_percent: normalizePrimitive(
      item.agent_commission_percent,
    ),
    listing_agent_commission_amount: normalizePrimitive(
      item.agent_commission_amount,
    ),
    listing_has_image: Boolean(
      item.image_url ||
        (Array.isArray(item.images) && item.images.length > 0),
    ),
    listing_is_assignment: Boolean(
      item.assignment?.enabled ||
        item.board_category === "assignment" ||
        item.board_subcategory === "assignment",
    ),
  };
};
